import { getOldUrl } from '@UI/aside/asideWebOldUrls/asideWebOldUrlsSelectors';
import {
  isContentTypeValidForWebTemplate,
  isTemplateForChildValid,
} from '@UI/aside/asideWebType/asideWebTypeSelectors';
import type { ContentHref, WebPage } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { mapDeletedNodesToUndefined } from '@newStore/documentApi/documentApiHelpers';
import {
  selectApiWithPendingChangesWithoutDeletes,
  selectContentItem,
  selectPathToRootWebConfigurations,
  selectWebPage,
} from '@newStore/documentApi/documentApiSelectors';
import { selectTypeNameSingle } from '@newStore/documentUI/nodeTypeConfigSelectors';
import { selectWebSites } from '@newStore/externalData/externalDataSelectors';
import { pathMap } from '@newStore/externalData/externalDataTypes';
import { createTypedSelector, isValidExternalUrl } from '@newStore/genericHelpers';
import { type EditComponent, isWebTabComponent } from '@nodeTypeConfig/configTypes';
import { isAfter } from 'date-fns';
import { get, pick } from 'lodash';
import { createError } from '../createError';
import { VALIDATION_PENDING, getNodeTypeLabel } from '../validationHelpers';
import type { AsideValidationRule } from '../validationTypes';

const reservedWebPaths = [
  '/zoeken',
  '/nieuwsoverzicht',
  '/professionalisering',
  '/localnews',
  '/newsDetail',
  '/view-options',
];
const unAllowedCharsRegex = /[<>%{}|\\^`:?#[\]@!$&()*+,;="]/;

const getInvalidCharacterMsg = (path: string, label: string) =>
  `<em>${path}</em> bevat een teken dat niet is toegelaten in <strong>${label}</strong>. Niet toegelaten tekens zijn: ? # [ ] @ ! $ & ( ) * + , ; = \\" < > % { } | \\ ^ \` `;

export const selectPathNoReservedWordsValidRule: AsideValidationRule = createTypedSelector(
  [
    (state, href) => selectWebPage(state, href)?.path,
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (path, config) => {
    if (reservedWebPaths.some((p) => path?.endsWith(p))) {
      return createError(
        'reservedPath',
        'selectPathNoReservedWordsValidRule',
        `<em>${path}</em> is voorbehouden voor een andere pagina. Kies een ander <strong>pad</strong>`,
        pick(config, ['component', 'property']),
      );
    }
    return true;
  },
);

export const selectPathNoUnAllowedCharsRule: AsideValidationRule = createTypedSelector(
  [
    (state, href) => selectWebPage(state, href)?.path,
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (path, config) => {
    if (path && unAllowedCharsRegex.test(path)) {
      return createError(
        'illegalCharactersInPath',
        'selectPathNoUnAllowedCharsRule',
        getInvalidCharacterMsg(path, 'het pad'),
        pick(config, ['component', 'property']),
      );
    }
    return true;
  },
);

export const selectOldUrlsNoUnAllowedCharsRule: AsideValidationRule = createTypedSelector(
  [
    (state, href) => selectWebPage(state, href)?.oldLocations,
    (state) => selectWebSites(state),
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (oldLocations, webSites, config) => {
    const invalidLocations = oldLocations
      ?.map((loc) => {
        if (unAllowedCharsRegex.test(loc.path)) {
          return loc;
        }
        return null;
      })
      .filter(Boolean) as WebPage['oldLocations'];

    if (invalidLocations?.length) {
      return createError(
        'illegalCharactersInPath',
        'selectPathNoUnAllowedCharsRule',
        invalidLocations
          .map((z) => getInvalidCharacterMsg(getOldUrl(z, webSites), "de vorige url's"))
          .join('<br />'),
        pick(config, ['component', 'property']),
      );
    }

    return true;
  },
);

export const selectPathUniqueWithinDocumentRule: AsideValidationRule = (
  state: RootState,
  href: ContentHref,
  parentHref: ContentHref | undefined,
  config: EditComponent,
) => {
  const webPage = selectWebPage(state, href);
  if (!webPage) {
    return true;
  }
  const allWebPages = selectApiWithPendingChangesWithoutDeletes(state).webPages;

  const duplicateWebPagePathInThisDoc = allWebPages.find(
    (wp) => wp.key !== webPage.key && wp.path === webPage.path,
  );
  if (duplicateWebPagePathInThisDoc) {
    const otherContentItem = selectContentItem(state, duplicateWebPagePathInThisDoc.source.href);
    const typeName = selectTypeNameSingle(state, otherContentItem.$$meta.permalink);

    return createError(
      'duplicatePathInDocument',
      'isWebPathUnique',
      `Binnen dit thema is het pad <strong>${
        webPage.path
      }</strong> al toegekend aan ${getNodeTypeLabel(typeName.split(':')[0])} "${
        otherContentItem.title
      }". Kies een ander pad`,
      pick(config, ['component', 'property']),
    );
  }
  return true;
};

export const selectPathFormatValidRule: AsideValidationRule = createTypedSelector(
  [
    (state, href) => selectWebPage(state, href)?.path,
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (path, config) => {
    if (path && !path.startsWith('/')) {
      return createError(
        'pathStartWithSlash',
        'selectPathFormatValidRule',
        `${
          config.definiteArticle ? 'het' : 'de'
        } ${config.label.toLocaleLowerCase()} moet starten met een <strong>/</strong>`,
        pick(config, ['component', 'property']),
      );
    }
    return true;
  },
);

export const selectIsBannerFieldRequired: AsideValidationRule = createTypedSelector(
  [
    (state, href: ContentHref) => selectWebPage(state, href),
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (webPage, config) => {
    if (!isWebTabComponent(config)) {
      console.error('Edit config is not for webPage', config);
      return true;
    }
    if (webPage?.options?.banner?.highlight) {
      const value = get(webPage, config.property);
      if (!value) {
        return createError(
          `banner${config.property}IsRequired`,
          'selectIsBannerFieldRequired',
          `Vul ${config.definiteArticle ? 'het' : 'de'} ${config.label.toLocaleLowerCase()} in.`,
          pick(config, ['component', 'property']),
        );
      }
    }

    return true;
  },
);

export const selectIsBannerDateValid: AsideValidationRule = createTypedSelector(
  [
    (state, href: ContentHref) => selectWebPage(state, href),
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (webPage, config) => {
    if (!isWebTabComponent(config)) {
      console.error('Edit config is not for webPage', config);
      return true;
    }
    const from = webPage?.options?.banner?.from;
    const to = webPage?.options?.banner?.to;
    if (from && to && isAfter(from, to)) {
      return createError(
        'bannerDateIsValid',
        'selectIsBannerDateValid',
        'De begindatum moet voor de einddatum liggen.',
        pick(config, ['component', 'property']),
      );
    }
    return true;
  },
);

export const selectIsValidBannerUrl: AsideValidationRule = createTypedSelector(
  [
    (state, href) => selectWebPage(state, href)?.options?.banner?.url,
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (url, config) => {
    if (url && !isValidExternalUrl(url)) {
      return createError(
        'bannerUrlIsValid',
        'selectIsValidBannerUrl',
        `Je moet een geldige ${config.label.toLocaleLowerCase()} invullen.`,
        pick(config, ['component', 'property']),
      );
    }
    return true;
  },
);

export const selectIsContentTypeValidForWebTemplateRule: AsideValidationRule = createTypedSelector(
  [
    (state, href: ContentHref) => selectWebPage(state, href),
    (state, href: ContentHref) => selectContentItem(state, href),
    (state) => state.externalData.data[pathMap.webTemplates]?.items,
    (state, href: ContentHref) => selectTypeNameSingle(state, href),
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (webPage, content, webTemplateMap, typeName, config) => {
    if (!Object.keys(webTemplateMap).length) {
      // when initialising web templates might not be loaded yet => validity is unkonwn
      return VALIDATION_PENDING;
    }

    if (!webPage || !content || webPage.$$meta.deleted) {
      return true;
    }

    const webTemplate = webTemplateMap[webPage.template.href];
    if (!webTemplate) {
      return true;
    }

    if (!isContentTypeValidForWebTemplate(webTemplate, content)) {
      return createError(
        'webConfigInvalidForNodeType',
        'selectIsContentTypeValidForWebTemplateRule',
        `${webTemplate.name} is geen geldige webconfiguratie voor een ${typeName}.`,
        pick(config, ['component', 'property']),
      );
    }

    return true;
  },
);

/**
 * read webTemplate.allowedTemplatesForChildNode of the parent to see if webPage of the node is valid
 * -> if template says there are allowedTemplatesForChildNode it should have a webConfiguration
 * -> and the webConfiguration needs to be one of the specified allowedTemplatesForChildNode
 */
export const selectIsWebTemplateForChildValidRule: AsideValidationRule = createTypedSelector(
  [
    (state, href: ContentHref) => selectWebPage(state, href),
    (state, href: ContentHref) => selectPathToRootWebConfigurations(state, href),
    (state) => state.externalData.data[pathMap.webTemplates]?.items,
    (state, href: ContentHref) => selectTypeNameSingle(state, href),
    (state, href, parentHref, config: EditComponent) => config,
  ],
  (webPage, pathToRoot, webTemplateMap, typeName, config) => {
    if (!Object.keys(webTemplateMap).length) {
      // when initialising web templates might not be loaded yet => validity is unkonwn
      return VALIDATION_PENDING;
    }

    const parentWebConfig = mapDeletedNodesToUndefined(pathToRoot)[1];
    const parentWebTemplate = parentWebConfig && webTemplateMap[parentWebConfig.template.href];
    if (!parentWebTemplate) {
      return true;
    }

    if (!webPage && parentWebTemplate.allowedTemplatesForChildNode?.length && config.required) {
      return createError(
        'webConfigRequiredForParentWebConfig',
        'selectIsWebTemplateForChildValidRule',
        `Een titel onder ${getNodeTypeLabel(
          parentWebTemplate.name,
        )} moet <strong>webconfiguratie</strong> hebben`,
        pick(config, ['component', 'property']),
      );
    }

    if (!webPage || webPage.$$meta.deleted) {
      return true;
    }

    const webTemplate = webTemplateMap[webPage?.template.href];
    if (!webTemplate) {
      return true;
    }

    if (!isTemplateForChildValid(webTemplate, parentWebTemplate)) {
      let message = '';
      if (!parentWebTemplate.allowedTemplatesForChildNode?.length) {
        message = `Titels onder een ${getNodeTypeLabel(
          parentWebTemplate.name,
        )} mogen <strong>geen webconfiguratie</strong> hebben`;
      } else if (parentWebTemplate.allowedTemplatesForChildNode.length === 1) {
        message = `"${
          webTemplate.name
        }" is geen geldig <strong>type webconfiguratie</strong>. Een titel onder een ${getNodeTypeLabel(
          parentWebTemplate.name.split('(')[0].trim(),
        )} moet webconfiguratie van type <strong>${
          webTemplateMap[parentWebTemplate.allowedTemplatesForChildNode[0]].name
        }</strong> hebben.`;
      } else {
        const possibleTypes = parentWebTemplate.allowedTemplatesForChildNode
          .map((tHref) => webTemplateMap[tHref].name)
          .join(', ')
          .replace(/,(?=[^,]+$)/, ' of ');
        message = `"${
          webTemplate.name
        }" is geen geldig <strong>type webconfiguratie</strong>. Mogelijke types webconfiguratie voor een ${getNodeTypeLabel(
          parentWebTemplate.name.split('(')[0].trim(),
        )} zijn: <em>${possibleTypes}</em>`;
      }
      return createError(
        'webConfigMismatchForParentWebConfig',
        'selectIsWebTemplateForChildValidRule',
        message,
        pick(config, ['component', 'property']),
      );
    }

    return true;
  },
);
